import { create } from 'zustand';
import { ForumCategory, ForumTopic, ForumReply, ForumCategoriesResponse, ForumTopicsResponse, ForumRepliesResponse } from '@/types';
import { fetchStrapiCollection, fetchStrapiCollectionByIds } from '@/lib/strapi';

interface CreateReplyParams {
  topicDocumentId: string;
  content: string;
  parentReplyDocumentId?: string;
}

interface CreateTopicParams {
  title: string;
  content: string;
  categoryDocumentId: string;
  groupId?: number;
  groupDocumentId?: string; // Use documentId instead of id for Strapi 5
  eventDocumentId?: string;
  volunteerDocumentId?: string;
}

interface ForumState {
  categories: ForumCategory[];
  topics: ForumTopic[];
  groupTopics: Record<number, ForumTopic[]>;
  eventTopics: Record<string, ForumTopic[]>;
  volunteerTopics: Record<string, ForumTopic[]>;
  repliesByTopic: Record<string, ForumReply[]>;
  selectedCategory: string | null;
  loading: boolean;
  error: string | null;
  fetchCategories: () => Promise<void>;
  fetchTopics: (categorySlug?: string) => Promise<void>;
  fetchGroupTopics: (groupId: number, categorySlug?: string) => Promise<void>;
  fetchEventTopics: (eventDocumentId: string, categorySlug?: string) => Promise<void>;
  fetchVolunteerTopics: (volunteerDocumentId: string, categorySlug?: string) => Promise<void>;
  fetchReplies: (topicDocumentId: string) => Promise<void>;
  createReply: (params: CreateReplyParams) => Promise<boolean>;
  updateReply: (topicDocumentId: string, replyDocumentId: string, content: string) => Promise<boolean>;
  deleteReply: (topicDocumentId: string, replyDocumentId: string) => Promise<boolean>;
  createTopic: (params: CreateTopicParams) => Promise<boolean>;
  deleteTopic: (topicDocumentId: string, groupId?: number, eventDocumentId?: string, volunteerDocumentId?: string) => Promise<boolean>;
  toggleLike: (topicDocumentId: string, groupId?: number, eventDocumentId?: string, volunteerDocumentId?: string) => Promise<void>;
  hasUserLiked: (topicDocumentId: string, userId: number, groupId?: number, eventDocumentId?: string, volunteerDocumentId?: string) => boolean;
  toggleReplyLike: (topicDocumentId: string, replyDocumentId: string) => Promise<void>;
  hasUserLikedReply: (topicDocumentId: string, replyDocumentId: string, userId: number) => boolean;
  setSelectedCategory: (slug: string | null) => void;
  getSortedTopics: () => ForumTopic[];
}

const baseUrl = '/api/strapi';

async function fetchTopicsWithReplyCounts(filters: Record<string, unknown>, errorMessage: string): Promise<ForumTopic[]> {
  let topics: ForumTopicsResponse['data'];
  try {
    topics = await fetchStrapiCollection<ForumTopicsResponse['data'][number]>('forum-topics', {
      populate: {
        category: { fields: ['name', 'slug', 'color'] },
        forum_replies: { count: true },
        author: { fields: ['id', 'displayName', 'username', 'initials', 'avatarUrl', 'avatarColor'] },
        liked_by: { fields: ['id'] },
      },
      sort: ['createdAt:desc'],
      filters,
    });
  } catch {
    throw new Error(errorMessage);
  }

  const topicDocumentIds = topics.map((topic) => topic.documentId);
  let replyCountByTopic: Record<string, number> = {};

  if (topicDocumentIds.length > 0) {
    // Reply counts are best-effort: a failure here must not fail the topic list.
    try {
      // Chunked for the same reason as newsStore.fetchCommentsBatch: one `$in`
      // over every topic id grows the query string without bound and AWS WAF
      // rejects it past 2048 bytes, silently zeroing every reply count
      // (MWBI-934). 66 topics already build a 4145-byte URL.
      const replies = await fetchStrapiCollectionByIds<{ topic?: { documentId?: string } | null }>(
        'forum-replies',
        topicDocumentIds,
        (documentIds) => ({
          filters: {
            topic: { documentId: { $in: documentIds } },
            deletedAt: { $null: true },
          },
          populate: {
            topic: { fields: ['documentId'] },
          },
        })
      );
      replyCountByTopic = replies.reduce<Record<string, number>>((acc, reply) => {
        const topicId = reply.topic?.documentId;
        if (topicId) {
          acc[topicId] = (acc[topicId] || 0) + 1;
        }
        return acc;
      }, {});
    } catch (err) {
      console.error('Error fetching forum reply counts:', err);
    }
  }

  return topics.map((topic) => ({
    ...topic,
    replies: replyCountByTopic[topic.documentId] || 0,
  }));
}

export const useForumStore = create<ForumState>((set, get) => ({
  categories: [],
  topics: [],
  groupTopics: {},
  eventTopics: {},
  volunteerTopics: {},
  repliesByTopic: {},
  selectedCategory: null,
  loading: false,
  error: null,

  fetchCategories: async () => {
    try {
      set({ loading: true, error: null });
      const categories = await fetchStrapiCollection<ForumCategoriesResponse['data'][number]>('forum-categories');
      set({ categories, loading: false });
    } catch (err) {
      console.error('forumStore.fetchCategories failed', err);
      set({ error: err instanceof Error ? err.message : 'Error fetching categories', loading: false });
    }
  },

  fetchTopics: async (categorySlug?: string) => {
    try {
      set({ loading: true, error: null });

      const filters: Record<string, unknown> = {
        group: { id: { $null: true } },
        event: { id: { $null: true } },
        volunteerOpportunity: { id: { $null: true } },
        deletedAt: { $null: true },
      };
      if (categorySlug) {
        filters.category = { slug: categorySlug };
      }

      const topics = await fetchTopicsWithReplyCounts(filters, 'Failed to fetch topics');
      set({ topics, loading: false });
    } catch (err) {
      console.error('forumStore.fetchTopics failed', err);
      set({ error: err instanceof Error ? err.message : 'Error fetching topics', loading: false });
    }
  },

  fetchGroupTopics: async (groupId: number, categorySlug?: string) => {
    try {
      set({ loading: true, error: null });

      const filters: Record<string, unknown> = {
        group: { id: { $eq: groupId } },
        event: { id: { $null: true } },
        deletedAt: { $null: true },
      };
      if (categorySlug) {
        filters.category = { slug: categorySlug };
      }

      const topicsWithReplies = await fetchTopicsWithReplyCounts(filters, 'Failed to fetch group topics');
      set((state) => ({
        groupTopics: { ...state.groupTopics, [groupId]: topicsWithReplies },
        loading: false
      }));
    } catch (err) {
      console.error('forumStore.fetchGroupTopics failed', err);
      set({ error: err instanceof Error ? err.message : 'Error fetching group topics', loading: false });
    }
  },

  fetchEventTopics: async (eventDocumentId: string, categorySlug?: string) => {
    try {
      set({ loading: true, error: null });

      const filters: Record<string, unknown> = {
        event: { documentId: { $eq: eventDocumentId } },
        deletedAt: { $null: true },
      };
      if (categorySlug) {
        filters.category = { slug: categorySlug };
      }

      const topics = await fetchTopicsWithReplyCounts(filters, 'Failed to fetch event topics');
      set((state) => ({
        eventTopics: { ...state.eventTopics, [eventDocumentId]: topics },
        loading: false,
      }));
    } catch (err) {
      console.error('src/store/forumStore.ts', err);
      set({ error: err instanceof Error ? err.message : 'Error fetching event topics', loading: false });
    }
  },

  fetchVolunteerTopics: async (volunteerDocumentId: string, categorySlug?: string) => {
    try {
      set({ loading: true, error: null });

      const filters: Record<string, unknown> = {
        volunteerOpportunity: { documentId: { $eq: volunteerDocumentId } },
        deletedAt: { $null: true },
      };
      if (categorySlug) {
        filters.category = { slug: categorySlug };
      }

      const topics = await fetchTopicsWithReplyCounts(filters, 'Failed to fetch volunteer topics');
      set((state) => ({
        volunteerTopics: { ...state.volunteerTopics, [volunteerDocumentId]: topics },
        loading: false,
      }));
    } catch (err) {
      console.error('src/store/forumStore.ts', err);
      set({ error: err instanceof Error ? err.message : 'Error fetching volunteer topics', loading: false });
    }
  },

  fetchReplies: async (topicDocumentId: string) => {
    try {
      const replies = await fetchStrapiCollection<ForumRepliesResponse['data'][number]>('forum-replies', {
        filters: { topic: { documentId: topicDocumentId }, deletedAt: { $null: true } },
        populate: {
          author: { fields: ['id', 'displayName', 'username', 'initials', 'avatarUrl', 'avatarColor'] },
          liked_by: { fields: ['id'] },
          parentReply: { fields: ['documentId'] },
        },
        sort: ['createdAt:asc'],
      });
      set((state) => ({
        repliesByTopic: { ...state.repliesByTopic, [topicDocumentId]: replies }
      }));
    } catch (err) {
      console.error('Error fetching replies:', err);
    }
  },

  createReply: async ({ topicDocumentId, content, parentReplyDocumentId }) => {
    try {
      const res = await fetch(`${baseUrl}/forum-replies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            content,
            topic: topicDocumentId,
            parentReply: parentReplyDocumentId || null,
          }
        }),
      });

      if (!res.ok) throw new Error('Failed to create reply');

      await get().fetchReplies(topicDocumentId);
      return true;
    } catch (err) {
      console.error('Error creating reply:', err);
      return false;
    }
  },

  updateReply: async (topicDocumentId: string, replyDocumentId: string, content: string) => {
    try {
      const res = await fetch(`${baseUrl}/forum-replies/${replyDocumentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            content,
          }
        }),
      });

      if (!res.ok) throw new Error('Failed to update reply');

      await get().fetchReplies(topicDocumentId);
      return true;
    } catch (err) {
      console.error('Error updating reply:', err);
      return false;
    }
  },

  deleteReply: async (topicDocumentId: string, replyDocumentId: string) => {
    try {
      const res = await fetch(`${baseUrl}/forum-replies/${replyDocumentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            deletedAt: new Date().toISOString(),
          }
        }),
      });

      if (!res.ok) throw new Error('Failed to delete reply');

      await get().fetchReplies(topicDocumentId);
      return true;
    } catch (err) {
      console.error('Error deleting reply:', err);
      return false;
    }
  },

  createTopic: async ({ title, content, categoryDocumentId, groupId, groupDocumentId, eventDocumentId, volunteerDocumentId }) => {
    try {
      const topicData: Record<string, unknown> = {
        title,
        content: content ? [{ type: 'paragraph', children: [{ type: 'text', text: content }] }] : [],
        likes: 0,
        replies: 0,
        category: categoryDocumentId,
      };

      // Use documentId for group relation (Strapi 5 best practice)
      if (groupDocumentId) {
        topicData.group = { connect: [groupDocumentId] };
      } else if (groupId) {
        // Fallback to numeric id if documentId not provided (for backwards compatibility)
        topicData.group = groupId;
      }

      if (eventDocumentId) {
        topicData.event = { connect: [eventDocumentId] };
      }

      if (volunteerDocumentId) {
        topicData.volunteerOpportunity = { connect: [volunteerDocumentId] };
      }

      const res = await fetch(`${baseUrl}/forum-topics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: topicData }),
      });

      if (!res.ok) throw new Error('Failed to create topic');

      // Refetch topics
      if (eventDocumentId) {
        await get().fetchEventTopics(eventDocumentId, get().selectedCategory ?? undefined);
      } else if (volunteerDocumentId) {
        await get().fetchVolunteerTopics(volunteerDocumentId, get().selectedCategory ?? undefined);
      } else if (groupId) {
        await get().fetchGroupTopics(groupId, get().selectedCategory ?? undefined);
      } else {
        await get().fetchTopics(get().selectedCategory ?? undefined);
      }
      return true;
    } catch (err) {
      console.error('Error creating topic:', err);
      return false;
    }
  },

  deleteTopic: async (topicDocumentId: string, groupId?: number, eventDocumentId?: string, volunteerDocumentId?: string) => {
    try {
      const res = await fetch(`${baseUrl}/forum-topics/${topicDocumentId}/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!res.ok) throw new Error('Failed to delete topic');

      // Remove the deleted topic from local state
      set((state) => {
        const removeTopic = (topics: ForumTopic[]) =>
          topics.filter((t) => t.documentId !== topicDocumentId);

        return {
          topics: removeTopic(state.topics),
          groupTopics: groupId
            ? { ...state.groupTopics, [groupId]: removeTopic(state.groupTopics[groupId] || []) }
            : state.groupTopics,
          eventTopics: eventDocumentId
            ? { ...state.eventTopics, [eventDocumentId]: removeTopic(state.eventTopics[eventDocumentId] || []) }
            : state.eventTopics,
          volunteerTopics: volunteerDocumentId
            ? { ...state.volunteerTopics, [volunteerDocumentId]: removeTopic(state.volunteerTopics[volunteerDocumentId] || []) }
            : state.volunteerTopics,
        };
      });
      return true;
    } catch (err) {
      console.error('Error deleting topic:', err);
      return false;
    }
  },

  toggleLike: async (topicDocumentId: string, groupId?: number, eventDocumentId?: string, volunteerDocumentId?: string) => {
    try {
      const res = await fetch(`${baseUrl}/forum-topics/${topicDocumentId}/toggle-like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!res.ok) throw new Error('Failed to toggle like');

      const { data } = await res.json();

      const updateTopicCollection = (topics: ForumTopic[] = []) =>
        topics.map((topic) =>
          topic.documentId === topicDocumentId
            ? { ...topic, likes: data.likeCount, liked_by: data.liked_by ?? (data.liked ? [...(topic.liked_by || [])] : []) }
            : topic
        );

      set((state) => {
        const updatedGroupTopics = groupId
          ? { ...state.groupTopics, [groupId]: updateTopicCollection(state.groupTopics[groupId] || []) }
          : state.groupTopics;
        const updatedEventTopics = eventDocumentId
          ? { ...state.eventTopics, [eventDocumentId]: updateTopicCollection(state.eventTopics[eventDocumentId] || []) }
          : state.eventTopics;
        const updatedVolunteerTopics = volunteerDocumentId
          ? { ...state.volunteerTopics, [volunteerDocumentId]: updateTopicCollection(state.volunteerTopics[volunteerDocumentId] || []) }
          : state.volunteerTopics;

        return {
          groupTopics: updatedGroupTopics,
          eventTopics: updatedEventTopics,
          volunteerTopics: updatedVolunteerTopics,
          topics: updateTopicCollection(state.topics),
        };
      });
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  },

  hasUserLiked: (topicDocumentId: string, userId: number, groupId?: number, eventDocumentId?: string, volunteerDocumentId?: string) => {
    const topic = volunteerDocumentId
      ? get().volunteerTopics[volunteerDocumentId]?.find(t => t.documentId === topicDocumentId)
      : eventDocumentId
        ? get().eventTopics[eventDocumentId]?.find(t => t.documentId === topicDocumentId)
      : groupId
        ? get().groupTopics[groupId]?.find(t => t.documentId === topicDocumentId)
        : get().topics.find(t => t.documentId === topicDocumentId);
    return topic?.liked_by?.some(u => u.id === userId) ?? false;
  },

  toggleReplyLike: async (topicDocumentId: string, replyDocumentId: string) => {
    try {
      const res = await fetch(`${baseUrl}/forum-replies/${replyDocumentId}/toggle-like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!res.ok) throw new Error('Failed to toggle reply like');

      const { data } = await res.json();

      set((state) => ({
        repliesByTopic: {
          ...state.repliesByTopic,
          [topicDocumentId]: (state.repliesByTopic[topicDocumentId] || []).map(r =>
            r.documentId === replyDocumentId
              ? { ...r, likes: data.likeCount, liked_by: data.liked_by ?? (data.liked ? [...(r.liked_by || [])] : []) }
              : r
          ),
        },
      }));
    } catch (err) {
      console.error('Error toggling reply like:', err);
    }
  },

  hasUserLikedReply: (topicDocumentId: string, replyDocumentId: string, userId: number) => {
    const replies = get().repliesByTopic[topicDocumentId] || [];
    const reply = replies.find(r => r.documentId === replyDocumentId);
    return reply?.liked_by?.some(u => u.id === userId) ?? false;
  },

  setSelectedCategory: (slug: string | null) => {
    set({ selectedCategory: slug });
    get().fetchTopics(slug ?? undefined);
  },

  getSortedTopics: () => {
    const topics = get().topics;

    const sortByDate = (a: ForumTopic, b: ForumTopic) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

    return [...topics].sort(sortByDate);
  },
}));

